<template>
  <main
    class="followday-page"
    role="main"
    aria-labelledby="followday-h1"
  >
    <div class="page-header">
      <h1
        id="followday-h1"
        class="h1"
      >
        fehlende Schüler am Folgetag
      </h1>
      <span>({{ formatDate(serverDayYmd || nextDayYmd) }})</span>
    </div>

    <div class="card">
      <!-- Filter -->
      <div class="filters">
        <div class="field field--search">
          <label for="ntf-search">Schülersuche</label>
          <input
            id="ntf-search"
            v-model="q"
            class="field"
            placeholder="Schüler"
            autocomplete="off"
            @keyup.enter="/* clientseitig */ null"
          >
        </div>
        <div class="field field--group">
          <label for="fd-group">Gruppe</label>
          <select
            id="fd-group"
            v-model="groupFilter"
          >
            <option value="">
              Alle
            </option>
            <option
              v-for="g in sortedGroupOptions"
              :key="g.id"
              :value="String(g.id)"
            >
              {{ g.name }}
            </option>
          </select>
        </div>
        <div class="field field--reset">
          <button
            class="btn btn-primary d-inline"
            :disabled="!q && !groupFilter"
            @click="resetFilters"
          >
            Reset
          </button>
        </div>
      </div>

      <div>
        <!-- Tabelle: Desktop/Tablet View -->
        <div class="followday-table">
          <div class="table-header column-6">
            <div
              class="sortable pl-2"
              role="button"
              tabindex="0"
              @click="setSort('studentName')"
              @keydown.enter="setSort('studentName')"
            >
              Schüler
              <span v-if="sortKey==='studentName'">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
            </div>
            <div>Betreff</div>
            <div>Absender</div>
            <div
              class="sortable"
              role="button"
              tabindex="0"
              @click="setSort('sentAt')"
              @keydown.enter="setSort('sentAt')"
            >
              Gesendet
              <span v-if="sortKey==='sentAt'">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
            </div>
            <div>Status</div>
            <div class="text-right pr-2">
              Aktion
            </div>
          </div>

          <!-- Zeilen -->
          <div
            v-for="row in pagedItems"
            :key="row.studentId"
            :class="['table-row', 'column-6', row.status === 'abgelehnt' ? 'status-cancel' : '']"
          >
            <div class="pl-2">
              <strong>{{ row.studentName }}</strong>
            </div>
            <div class="muted subject">
              {{ row.subject || '' }}
              <template v-if="row.timeRangeText">
                <span class="font-bold text-black-alpha-90 mr-1"> ({{ row.timeRangeText }})</span>
              </template>
              <i
                v-if="row.hasAttachment"
                class="pi pi-file-pdf"
                aria-hidden="true"
                style="font-size:14px;color:var(--danger);vertical-align: baseline;"
              />
            </div>
            <div class="muted">
              {{ row.sender || '—' }}
            </div>
            <div class="muted">
              {{ formatDateTime(row.sentAt) }}
            </div>
            <div>
              <span
                class="badge"
                :class="badgeClass(row.status)"
              >{{ statusLabel(row.status) }}</span>
            </div>
            <div class="row-actions pr-2">
              <button
                class="icon-btn open-view"
                :title="'Meldung ansehen'"
                @click="openView(row._n || row)"
              >
                <i
                  class="pi pi-eye"
                  aria-hidden="true"
                  style="font-size:18px;color:var(--muted);"
                />
              </button>
            </div>
          </div>

          <!-- Skeleton -->
          <div
            v-if="loading"
            class="table-row column-6 skeleton"
          >
            <div>
              <div class="sk-line" />
              <div class="sk-line short" />
            </div>
            <div>
              <div class="sk-line" />
            </div>
            <div>
              <div class="sk-line short" />
            </div>
            <div>
              <div class="sk-line short" />
            </div>
            <div>
              <div class="sk-badge" />
            </div>
            <div class="text-right">
              <div class="sk-btn" />
            </div>
          </div>
        </div>

        <!-- Mobile: Cards statt Tabelle -->
        <div class="followday-cards">
          <div
            v-for="row in pagedItems"
            :key="`card-${row.studentId}`"
            class="followday-card"
          >
            <div class="card-top">
              <div class="card-title">
                <strong class="card-student">{{ row.studentName }}</strong>
              </div>
              <span
                class="badge"
                :class="badgeClass(row.status)"
              >{{ statusLabel(row.status) }}</span>
            </div>

            <div class="card-subject">
              <span
                class="muted"
                style="font-size: 15px; font-weight: 500;"
              >{{ row.subject || '—' }}</span>
              <span class="card-icons">
                <i
                  v-if="row.hasAttachment"
                  class="pi pi-file-pdf"
                  aria-hidden="true"
                  style="font-size:14px;color:var(--danger);vertical-align: baseline;"
                />
              </span>
            </div>

            <div class="card-meta">
              <div class="meta">
                <span v-if="row.timeRangeText">
                  <span class="muted font-semibold">Zeitraum: </span>{{ row.timeRangeText }}
                </span>
              </div>
            </div>

            <div class="card-actions">
              <div
                class="meta"
                style="line-height: 16px;"
              >
                <span><span class="muted font-semibold">Gesendet: </span>{{ formatDateTime(row.sentAt) }}</span>
                <span class="d-block"><span class="muted font-semibold">von: </span>{{ row.sender || '—' }}</span>
              </div>
              <button
                class="btn btn-primary card-open d-inline"
                @click="openView(row._n || row)"
              >
                Öffnen
              </button>
            </div>
          </div>
        </div>

        <p
          v-if="!loading && sortedItems.length === 0"
          class="muted"
        >
          Keine Einträge.
        </p>
        <p
          v-if="error"
          class="notice notice-danger mt-12"
          role="alert"
        >
          {{ error }}
        </p>
      </div>

      <!-- Pager -->
      <div
        class="mt-12"
        style="display:flex;align-items:center;gap:8px;justify-content:flex-end;"
      >
        <span
          class="muted pagination-item"
          style="margin-right:auto"
        >Gesamt: {{ sortedItems.length }}</span>
        <label
          class="muted pagination-item"
          for="fd-limit"
        >pro Seite:</label>
        <select
          id="fd-limit"
          class="pagination-item"
          :value="limit"
          @change="changeLimit"
        >
          <option
            v-for="n in [10,20,50,100]"
            :key="n"
            :value="n"
          >
            {{ n }}
          </option>
        </select>
        <button
          class="btn"
          :disabled="page<=1"
          @click="prevPage"
        >
          Zurück
        </button>
        <span class="muted">Seite {{ page }} / {{ pages }}</span>
        <button
          class="btn"
          :disabled="page>=pages"
          @click="nextPage"
        >
          Weiter
        </button>
      </div>
    </div>

    <!-- Dialog: Meldung ansehen -->
    <NotificationViewDialog
      :open="viewOpen"
      :notification="selected"
      @close="closeView"
    />
  </main>
</template>
<script setup>
import {ref, computed, onMounted, watch} from 'vue';
import {fetchNotificationsForDay} from '@/api/notifications';
import {useAuthStore} from '@/stores/auth';
import {useRouter} from 'vue-router';
import NotificationViewDialog from '@/components/notifications/NotificationViewDialog.vue';

const auth = useAuthStore();
const roleId = computed(() => auth.user?.roleId ?? auth.user?.role_id ?? null);
const isAdmin = computed(() => roleId.value === 1);
const isTeacher = computed(() => roleId.value === 2);

const router = useRouter();

const loading = ref(false);
const error = ref('');

const notifications = ref([]); // Schüler mit Meldung (vom Backend gefiltert)
const groupOptions = ref([]);  // Gruppen für Filter (vom Backend geliefert)

// Filter
const q = ref('');
const groupFilter = ref('');

// Sortierung
const sortKey = ref('studentName');
const sortDir = ref('asc');

// Pagination
const page = ref(1);
const limit = ref(20);

function closeView() {
  viewOpen.value = false;
  selected.value = null;
  // Nach Schließen des Detail-Dialogs Liste neu laden
  load();
}

function setSort(key) {
  if (sortKey.value === key) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortKey.value = key;
    sortDir.value = key === 'sentAt' ? 'desc' : 'asc';
  }
}

function nextPage() {
  if (page.value < pages.value) page.value++;
}

function prevPage() {
  if (page.value > 1) page.value--;
}

function changeLimit(e) {
  limit.value = Number(e.target.value);
  page.value = 1;
}

function resetFilters() {
  q.value = '';
  groupFilter.value = '';
  page.value = 1;
}

// Dialog: Meldung ansehen
const viewOpen = ref(false);
const selected = ref(null);

function openView(n) {
  selected.value = n || null;
  viewOpen.value = true;
}

// Nächster Schultag (Mo–Fr)
function computeNextSchoolDay(base = new Date()) {
  const d = new Date(base);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 1);
  while (d.getDay() === 0 || d.getDay() === 6) {
    d.setDate(d.getDate() + 1);
  }
  return d;
}

const nextDay = computed(() => computeNextSchoolDay(new Date()));
const nextDayYmd = computed(() => {
  const d = nextDay.value;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
});
const serverDayYmd = ref('');

// Laden (nur Notifications; Schülerliste entfällt)

// Gruppen kommen direkt aus dem Backend-Payload (groupOptions)

async function loadNotifications() {
  try {
    const {day, data, groups} = await fetchNotificationsForDay();
    serverDayYmd.value = (typeof day === 'string' && day) ? day : nextDayYmd.value;
    notifications.value = Array.isArray(data) ? data : [];
    groupOptions.value = Array.isArray(groups) ? groups.map(g => ({id: String(g.id), name: String(g.name)})) : [];
  } catch (e) {
    throw new Error(e?.response?.data?.message || e?.message || 'Meldungen konnten nicht geladen werden.');
  }
}

async function load() {
  loading.value = true;
  error.value = '';
  try {
    await loadNotifications();
  } catch (e) {
    error.value = e?.message || 'Ladevorgang fehlgeschlagen.';
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  // Nur Admin/Lehrer sinnvoll
  if (!(isAdmin.value || isTeacher.value)) {
    router.replace({name: 'Dashboard'});
    return;
  }
  load();
});

// Utils
function parseDateLike(v) {
  if (!v) return null;
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}(?:$|T)/.test(v)) {
    const [y, m, d] = v.slice(0, 10).split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  const dt = new Date(v);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function formatDate(isoYmd) {
  const dt = parseDateLike(isoYmd);
  if (!dt) return '—';
  return new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).format(dt);
}

function formatDateTime(iso) {
  const dt = parseDateLike(iso);
  if (!dt) return '—';
  return new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).format(dt);
}

function formatTimeHHMM(v) {
  if (!v) return '';
  if (typeof v === 'string' && /^(\d{2}):(\d{2})(?::\d{2})?$/.test(v)) {
    const m = v.match(/^(\d{2}):(\d{2})/);
    return m ? `${m[1]}:${m[2]}` : '';
  }
  const d = v instanceof Date ? v : new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).format(d);
}

function timeRangeForNextDay(n) {
  // Server liefert bereits nur Meldungen, die den Folgetag betreffen.
  const tf = n?.timeFrom ?? n?.time_from ?? null;
  const tt = n?.timeTo ?? n?.time_to ?? null;
  const a = formatTimeHHMM(tf);
  const b = formatTimeHHMM(tt);
  if (a && b) return `${a}–${b}`;
  return a || b || '';
}

function statusLabel(status) {
  if (status === 'offen') return 'Offen';
  if (status === 'gelesen') return 'Gelesen';
  if (status === 'abgelehnt') return 'Abgelehnt';
  if (status === 'akzeptiert') return 'Akzeptiert';
  return status || '—';
}

function badgeClass(status) {
  return {
    'badge-warn': status === 'offen',
    'badge-info': status === 'gelesen',
    'badge-danger': status === 'abgelehnt',
    'badge-success': status === 'akzeptiert',
  };
}

// Gruppen kommen als Array [{id,name}] vom Backend (siehe groupOptions Ref)

const sortedGroupOptions = computed(() => {
  const base = Array.isArray(groupOptions.value) ? groupOptions.value : [];
  const arr = [...base];
  arr.sort((a, b) => {
    const aLG = String(a.name || '').startsWith('LG ');
    const bLG = String(b.name || '').startsWith('LG ');
    if (aLG !== bLG) return aLG ? -1 : 1;
    return String(a.name || '').localeCompare(String(b.name || ''), 'de', {sensitivity: 'base'});
  });
  return arr;
});

// Abgeleitete Zeilen direkt aus Backend-Meldungen
const baseRows = computed(() => {
  const list = Array.isArray(notifications.value) ? notifications.value : [];
  return list.map(n => {
    const studentId = n?.studentId ?? n?.student_id ?? null;
    const studentName = n?.studentName ?? n?.student_name ?? '';
    const subject = n?.subject ?? '';
    const tr = timeRangeForNextDay(n);
    const sender = n?.parent_name ?? n?.parentName ?? '';
    const sentAt = n?.sentAt ?? n?.sent_at ?? n?.createdAt ?? n?.created_at ?? null;
    const status = (n?.status ?? '').toString().toLowerCase().trim();
    const gids = Array.isArray(n?.groupIds) ? n.groupIds
        : (Array.isArray(n?.group_ids) ? n.group_ids : []);
    const hasAttachment = n.has_attachment === true;
    return {
      studentId,
      studentName,
      groups: gids.map(x => String(x)),
      flagged: true, // alle gelisteten Schüler haben Meldung
      subject,
      timeRangeText: tr,
      sender,
      sentAt,
      status,
      hasAttachment,
      _n: n
    };
  });
});

// Filter anwenden
const filtered = computed(() => {
  let arr = baseRows.value;
  const needle = q.value.trim().toLowerCase();
  if (needle) {
    arr = arr.filter(r => r.studentName && r.studentName.toLowerCase().includes(needle));
  }
  if (groupFilter.value) {
    const gv = String(groupFilter.value);
    arr = arr.filter(r => {
      const gs = Array.isArray(r.groups) ? r.groups.map(x => String(x)) : [];
      return gs.includes(gv) || gs.some(name => String(name) === gv);
    });
  }
  return arr;
});

// Sortierung: flagged nach oben, dann Key
const sortedItems = computed(() => {
  const arr = [...filtered.value];
  arr.sort((a, b) => {
    if (a.flagged !== b.flagged) return a.flagged ? -1 : 1;
    switch (sortKey.value) {
      case 'sentAt': {
        const va = a.sentAt ? new Date(a.sentAt).getTime() : 0;
        const vb = b.sentAt ? new Date(b.sentAt).getTime() : 0;
        return sortDir.value === 'asc' ? va - vb : vb - va;
      }
      case 'studentName':
      default:
        return (sortDir.value === 'asc' ? 1 : -1) * String(a.studentName || '').localeCompare(String(b.studentName || ''), 'de', {sensitivity: 'base'});
    }
  });
  return arr;
});

// Seitenanzahl und Slicing
const pages = computed(() => Math.max(1, Math.ceil(sortedItems.value.length / limit.value)));
const pagedItems = computed(() => {
  const start = (page.value - 1) * limit.value;
  return sortedItems.value.slice(start, start + limit.value);
});

// Reaktionen auf Filter
watch([q, groupFilter], () => {
  page.value = 1;
});
watch(pages, (p) => {
  if (page.value > p) page.value = p;
  if (page.value < 1) page.value = 1;
});
</script>

<style scoped>
.followday-page {
  background: var(--bg);
  color: var(--text);
}

.page-header {
  border-bottom: 1px solid var(--border);
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  display: inline-flex;
  align-items: first baseline;
  gap: 8px;
}

.card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, .04);
  margin-bottom: 1rem;
  padding: 12px;
}

.filters {
  display: grid;
  gap: 8px;
  align-items: end;
  margin-bottom: 12px;
  grid-template-columns: 1fr 200px 90px;
}

.filters .field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.filters .field label {
  font-size: 12px;
  color: var(--muted);
  margin: 0;
}

.filters .field input,
.filters .field select {
  height: 38px;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text);
  box-sizing: border-box;
  margin-bottom: 0;
}

.filters .field input:focus,
.filters .field select:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px var(--focus);
}

.filters .field input::placeholder {
  color: var(--muted);
}

.table-header.column-6,
.table-row.column-6 {
  display: grid;
  grid-template-columns: 1fr 1.5fr 1fr 160px 120px 80px;
  gap: 8px;
  align-items: center;
  font-size: 14px;
}

.table-header.column-6 {
  font-size: 12px;
  color: var(--muted);
  padding: 6px 0;
  border-bottom: 1px solid var(--border);
}

.table-row.column-6 {
  padding: 5px 0;
  border-bottom: 1px solid var(--border);
}

.table-row.status-cancel {
  background: rgba(245, 158, 11, .14);
}

.subject {
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}

.row-actions {
  display: inline-flex;
  gap: 8px;
  justify-content: flex-end;
  align-items: center;
}

.icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  width: 36px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text);
  cursor: pointer;
  transition: background-color 140ms, transform 80ms;
}

.icon-btn:hover:not(:disabled) {
  background: color-mix(in srgb, var(--primary) 8%, transparent);
}

.icon-btn:active:not(:disabled) {
  transform: translateY(1px);
}

.icon-btn:disabled {
  opacity: .6;
  cursor: default;
}

.sortable {
  cursor: pointer;
  user-select: none;
}

.sortable:hover {
  text-decoration: underline;
}

.badge {
  display: inline-block;
  font-size: 12px;
  line-height: 16px;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--muted);
}

.badge-success {
  color: var(--success);
  border-color: rgba(16, 185, 129, .5);
  background: rgba(16, 185, 129, .08);
}

.badge-warn {
  color: var(--warn);
  border-color: rgba(245, 158, 11, .5);
  background: rgba(245, 158, 11, .08);
}

.badge-info {
  color: var(--info);
  border-color: rgba(37, 99, 235, .5);
  background: rgba(37, 99, 235, .08);
}

.badge-danger {
  color: var(--danger);
  border-color: rgba(239, 68, 68, .5);
  background: rgba(239, 68, 68, .08);
}

.skeleton .sk-line {
  height: 12px;
  background: color-mix(in srgb, var(--bg) 50%, white);
  border-radius: 6px;
}

.skeleton .sk-line.short {
  width: 70%;
}

.skeleton .sk-badge {
  width: 64px;
  height: 18px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--bg) 50%, white);
}

.skeleton .sk-btn {
  width: 120px;
  height: 32px;
  border-radius: 8px;
  background: color-mix(in srgb, var(--bg) 50%, white);
}

/* Mobile Cards */
.followday-cards {
  display: none;
}

.followday-card {
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--surface);
  padding: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, .04);
}

.card-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;
}

.card-title {
  min-width: 0;
}

.card-student {
  display: inline-block;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card-subject {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;
}

.card-subject .muted {
  min-width: 0;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card-icons {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  flex: 0 0 auto;
}

.card-meta {
  display: grid;
  gap: 8px;
  margin-bottom: 8px;
}

.meta {
  font-size: 12px;
  color: var(--muted);
}

.d-block {
  display: block;
}

.font-semibold {
  font-weight: 600;
}

.card-actions {
  display: grid;
  grid-template-columns: auto 90px;
  gap: 8px;
  align-items: end;
}

.card-open {
  display: inline-block;
}

.d-inline {
  display: inline-block;
}

.pagination-item {
  display: none;
}

/* Responsive */
@media (max-width: 799px) {
  .filters {
    align-items: end;
    gap: 6px;
    border: 1px solid #ddd;
    border-radius: 8px;
    padding: 12px;
    background: whitesmoke;
    grid-template-columns: 1.2fr .8fr;
    margin-bottom: 20px;
  }

  .filters .field {
    width: 100%;
    margin-bottom: 6px;
  }

  .filters .field--search {
    grid-column: 1 / -1;
  }

  .filters .field--group {
    grid-column: 1 / 2;
  }

  .filters .field--reset {
    grid-column: 2 / 3;
    align-items: flex-end;
  }

  .filters .field input,
  .filters .field select,
  .filters .field button {
    height: 32px;
    font-size: 14px;
    padding: 0 10px !important;
    width: 100%;
  }

  .followday-table {
    display: none;
  }

  .followday-cards {
    display: grid;
    grid-template-columns: 1fr;
    gap: 10px;
    margin-bottom: 12px;
  }

  #followday-h1 {
    font-size: 18px;
  }

  .page-header > span {
    font-size: 12px;
  }
}

@media (max-width: 640px) {
  .card {
    padding: 12px;
  }

  .card-open {
    width: 100%;
  }
}
</style>